import React, { useState, useCallback, memo } from 'react';
import { Flex, Button, Input, Pagination, Spin } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { useSession } from 'next-auth/react';
import { ProductCard } from '@ozoneuniverse/ui-components/build-pkg/src/components/asset-store/products/ProductCard';
import { useGetBoughtAssetsByUserQuery } from '@src/components/generated/graphql';
import { debounce } from 'lodash';
import { isAssetExcluded, getAssetType } from '@src/utils/assetHelpers';
import ModelViewerModal from '@src/components/side-bar-menu/web2-asset-store/ModelViewer';
import styles from '../add-to-scene.module.css';

const FORMATS_TYPES = {
  MODEL: 2,
  IMAGE: 8,
  AUDIO: 6,
  VIDEO: 7,
};

const PAGE_LIMIT = 18;

const Cards = ({ assetsBought, onLoadAsset, loading, onOpenPreview }) => {
  return (
    <Flex gap={20} wrap='wrap'>
      {loading ? (
        <Spin
          indicator={
            <LoadingOutlined style={{ fontSize: 30, color: '#000' }} spin />
          }
        />
      ) : (
        <>
          {assetsBought?.length ? (
            assetsBought?.map((el, i) => {
              return (
                <ProductCard
                  product={el}
                  disableLink={true}
                  disableButtons={true}
                  disablePreview={el.assetURL.includes('AssetPack.gltf')}
                  type='web2'
                  imgOnClick={
                    el.assetURL.includes('AssetPack.gltf')
                      ? undefined
                      : () => onOpenPreview(el.assetURL)
                  }
                  customButton={{
                    text: 'Add to scene',
                    onClick: () => onLoadAsset(el),
                  }}
                  key={i}
                />
              );
            })
          ) : (
            <div className={styles.emptyInventory}>
              <div className={styles.emptyInventoryContent}>
                <div className={styles.emptyInventoryIcon}>📦</div>
                <h3>Your inventory is empty</h3>
                <p>
                  Start building your collection by purchasing assets from the
                  store or uploading your own files.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </Flex>
  );
};

const CardsMemo = memo(Cards);

export default function BoughtAssetsContent({ onAddToScene }) {
  const { data: session } = useSession();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchInput, setSearchInput] = useState<string | undefined>(undefined);
  const [active, setActive] = useState<number>(0);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [modelUrl, setModelUrl] = useState<string>('');

  const { data, loading } = useGetBoughtAssetsByUserQuery({
    variables: {
      where: {
        userId: session?.user.id ?? '',
        offset: PAGE_LIMIT * (currentPage - 1),
        limit: PAGE_LIMIT,
        filter: {
          type: filterType,
          searchInput: searchInput,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const assetsBought = data?.getBoughtAssetsByUser.assets?.filter(
    (el) => !isAssetExcluded(el.assetURL)
  );

  const onLoadAsset = useCallback(
    (asset) => {
      if (asset.assetURL.includes('AssetPack.gltf')) {
        return;
      }

      const assetType = getAssetType(asset.fileType);

      onAddToScene({
        type: assetType,
        name: asset.name,
        url: asset.assetURL,
        previewImage: asset.previewImage,
        marketId: asset.marketId,
      });
    },
    [onAddToScene]
  );

  const getResults = debounce(async (name) => {
    setSearchInput(name);
  }, 800);

  const onSearch = async (input) => {
    if (input.target.value.length <= 1) {
      getResults.cancel();
      return setSearchInput(undefined);
    }

    return getResults(input.target.value);
  };

  const onOpenPreview = useCallback((url) => {
    setPreviewOpen(true);
    setModelUrl(url);
  }, []);

  return (
    <div className={styles.contentWrapper}>
      {assetsBought?.length ? (
        <>
          <div className={styles.topSection}>
            <h3 className={styles.sectionTitle}>Your Bought Assets</h3>
            <Flex
              align='center'
              justify='space-between'
              style={{ marginBottom: 20 }}
            >
              <Flex align='flex-start' gap={25}>
                <Button
                  onClick={() => {
                    setActive(0);
                    setFilterType('all');
                  }}
                  className={`${active === 0 && styles.activeFilter} ${
                    styles.filterButton
                  }`}
                >
                  All
                </Button>
                <Button
                  onClick={() => {
                    setActive(1);
                    setFilterType('prefab');
                  }}
                  className={`${active === 1 && styles.activeFilter} ${
                    styles.filterButton
                  }`}
                >
                  Prefabs
                </Button>
                <Button
                  onClick={() => {
                    setActive(2);
                    setFilterType('single_asset');
                  }}
                  className={`${active === 2 && styles.activeFilter} ${
                    styles.filterButton
                  }`}
                >
                  Single assets
                </Button>
              </Flex>
              <Input
                style={{ width: 300 }}
                placeholder='Search assets'
                className={styles.searchInput}
                onChange={onSearch}
                prefix={<SearchOutlined />}
              />
            </Flex>
          </div>

          <CardsMemo
            assetsBought={assetsBought}
            onLoadAsset={onLoadAsset}
            loading={loading}
            onOpenPreview={onOpenPreview}
          />

          <Flex justify='center' style={{ marginTop: 20 }}>
            <Pagination
              current={currentPage}
              pageSize={PAGE_LIMIT}
              total={data?.getBoughtAssetsByUser.totalCount}
              hideOnSinglePage
              showSizeChanger={false}
              onChange={(page) => {
                setCurrentPage(page);
              }}
            />
          </Flex>
        </>
      ) : (
        <div className={styles.emptyInventoryWrapper}>
          <div className={styles.emptyInventory}>
            <div className={styles.emptyInventoryContent}>
              <div className={styles.emptyInventoryIcon}>📦</div>
              <h3>You don't have bought assets yet</h3>
              <p>
                Start building your collection by purchasing assets from the
                store.
              </p>
            </div>
          </div>
        </div>
      )}
      <ModelViewerModal
        open={previewOpen}
        setOpen={setPreviewOpen}
        modelUrl={modelUrl}
      />
    </div>
  );
}
